class StickyHeader extends HTMLElement {
  constructor() {
    super();

    this.setHeaderHeight = this.setHeaderHeight.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.stickyTopGap = 0;
    this.preventToggleScroll = false;
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    if (!this.header) return;

    const option = this.getAttribute('data-sticky-type');

    this.stickyNone = option === 'none';
    this.stickyAlways = option === 'always';
    this.stickyScrollUp = option === 'scroll-up';

    this.headerBounds = {};
    this.setHeaderHeight();

    const mediaQuery = window.matchMedia('(max-width: 990px)');
    mediaQuery.addEventListener('change', this.setHeaderHeight);

    if (this.stickyAlways) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;

    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll;
    this.hideHeaderOnScrollUp = () => {
      this.preventReveal = true;
    };

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);

    if (!this.stickyNone) {
      window.addEventListener('scroll', this.onScrollHandler, {
        passive: true,
      });
    }

    window.addEventListener('resize', this.onResize, {
      passive: true,
    });

    this.createObserver();
    this.updateStickyTopByHeaderState();
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.onResize);
  }

  setHeaderHeight() {
    if (!this.header) return;

    const headerHeight = this.header.offsetHeight;

    document.documentElement.style.setProperty(
      '--header-height',
      `${headerHeight}px`
    );

    const headerBottom = this.header.querySelector(
      '.header-bottom'
    );

    const bottomHeight = headerBottom
      ? headerBottom.offsetHeight
      : 0;

    document.documentElement.style.setProperty(
      '--header-height-abs-menu',
      `${bottomHeight}px`
    );

    this.updateStickyTopByHeaderState();
  }

  onResize() {
    this.setHeaderHeight();
    this.updateStickyTopByHeaderState();
  }

  createObserver() {
    const observer = new IntersectionObserver((entries, obs) => {
      this.headerBounds = entries[0].intersectionRect;
      obs.disconnect();
    });

    observer.observe(this.header);
  }

  updateStickyTopByHeaderState() {
    const root = document.documentElement;
    const headerHeight = this.header
      ? this.header.offsetHeight
      : 0;

    let topOffset = this.stickyTopGap;

    if (this.stickyNone) {
      topOffset = 0;
    }

    if (this.stickyAlways) {
      topOffset = this.stickyTopGap + headerHeight;
    }

    if (this.stickyScrollUp) {
      const isHeaderSticky = this.header.classList.contains(
        'shopify-section-header-sticky'
      );

      const isHeaderHidden = this.header.classList.contains(
        'shopify-section-header-hidden'
      );

      const isHeaderVisible =
        isHeaderSticky && !isHeaderHidden;

      topOffset = isHeaderVisible
        ? this.stickyTopGap + headerHeight
        : this.stickyTopGap;
    }

    root.style.setProperty(
      '--sticky-header-offset',
      `${topOffset}px`
    );
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.preventToggleScroll) {
      this.currentScrollTop = scrollTop;
      return;
    }

    if (!this.stickyNone) {
      if (scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('header-scroll-sticky');
      } else {
        this.header.classList.remove('header-scroll-sticky');
      }
    }

    if (this.stickyAlways) {
      this.updateStickyTopByHeaderState();
      return;
    }

    if (this.stickyNone) return;
    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      requestAnimationFrame(this.hide.bind(this));
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        this.preventReveal = false;
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this));
    }

    if (this.stickyScrollUp) {
      this.updateStickyTopByHeaderState();
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.stickyAlways || this.stickyNone) return;

    this.header.classList.add(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky'
    );

    this.closeMenuDisclosure();
    this.closeSearchModal();
    this.updateStickyTopByHeaderState();
  }

  reveal() {
    if (this.stickyAlways || this.stickyNone) return;

    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
    this.updateStickyTopByHeaderState();
  }

  reset() {
    if (this.stickyAlways) return;

    this.header.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate'
    );

    this.updateStickyTopByHeaderState();
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');

    this.disclosures.forEach((disclosure) => {
      if (typeof disclosure.close === 'function') {
        disclosure.close();
      }
    });
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');

    if (this.searchModal && typeof this.searchModal.close === 'function') {
      this.searchModal.close(false);
    }
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}


class ToggleHeaderBottom {
  constructor(toggle) {
    this.toggle = toggle;

    if (this.toggle.dataset.toggleHeaderBottomInit === 'true') return;

    this.header = toggle.closest('.header');

    this.headerBottom = this.header
      ? this.header.querySelector('.header-toogle_bottom')
      : document.querySelector('.header-toogle_bottom');

    if (!this.headerBottom) return;

    this.toggle.dataset.toggleHeaderBottomInit = 'true';

    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.init();
  }

  init() {
    this.toggle.addEventListener('click', this.onClick);
    this.toggle.addEventListener('keydown', this.onKeyDown);
  }

  onClick(event) {
    event.preventDefault();

    const stickyHeader = this.toggle.closest('sticky-header');

    if (stickyHeader) {
      stickyHeader.preventToggleScroll = true;
    }

    this.toggle.classList.toggle('active');
    this.headerBottom.classList.toggle('open');

    requestAnimationFrame(() => {
      if (!stickyHeader) return;

      stickyHeader.setHeaderHeight();

      stickyHeader.currentScrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      if (
        stickyHeader.stickyScrollUp &&
        stickyHeader.header.classList.contains('header-scroll-sticky')
      ) {
        stickyHeader.header.classList.add(
          'shopify-section-header-sticky'
        );

        stickyHeader.header.classList.remove(
          'shopify-section-header-hidden'
        );
      }

      stickyHeader.updateStickyTopByHeaderState();

      setTimeout(() => {
        stickyHeader.preventToggleScroll = false;
      }, 200);
    });
  }

  onKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onClick(event);
    }
  }
}

function initToggleHeaderBottom(container = document) {
  container.querySelectorAll('.toogle-header_bottom').forEach((toggle) => {
    new ToggleHeaderBottom(toggle);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initToggleHeaderBottom();
});

document.addEventListener('shopify:section:load', (event) => {
  initToggleHeaderBottom(event.target);
});

document.addEventListener('shopify:section:select', (event) => {
  initToggleHeaderBottom(event.target);
});


class HeaderDropdownMenu {
  constructor(nav) {
    this.nav = nav;

    if (this.nav.dataset.dropdownMenuInit === 'true') return;
    this.nav.dataset.dropdownMenuInit = 'true';

    this.onClick = this.onClick.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

    this.init();
  }

  get trigger() {
    return this.nav.dataset.dropdownTrigger || 'hover';
  }

  init() {
    this.nav.addEventListener('click', this.onClick);
    this.nav.addEventListener('mouseover', this.onMouseOver);
    this.nav.addEventListener('mouseout', this.onMouseOut);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    this.nav
      .querySelectorAll('.menu-item-has-children > .item-link, .header-sub-has-children > .item-link')
      .forEach((link) => {
        link.setAttribute('aria-haspopup', 'true');
        link.setAttribute('aria-expanded', 'false');
      });
  }

  onClick(event) {
    const link = event.target.closest(
      '.menu-item-has-children > .item-link, .header-sub-has-children > .item-link'
    );

    if (!link || !this.nav.contains(link)) return;

    const item = link.closest('.menu-item-has-children, .header-sub-has-children');

    if (!item) return;

    /*
      Top menu follows setting.
      Sub menu level 2 should still be clickable to open.
    */
    const isSubItem = item.classList.contains('header-sub-has-children');

    if (this.trigger !== 'click' && !isSubItem) return;

    event.preventDefault();
    event.stopPropagation();

    const isOpen = item.classList.contains('open');

    this.closeSiblings(item);

    if (isOpen) {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  onMouseOver(event) {
    if (this.trigger !== 'hover') return;

    const item = event.target.closest('.menu-item-has-children, .header-sub-has-children');

    if (!item || !this.nav.contains(item)) return;

    this.openItem(item);
  }

  onMouseOut(event) {
    if (this.trigger !== 'hover') return;

    const item = event.target.closest('.menu-item-has-children, .header-sub-has-children');

    if (!item || !this.nav.contains(item)) return;

    if (item.contains(event.relatedTarget)) return;

    this.closeItem(item);
  }

  onDocumentClick(event) {
    if (!this.nav.contains(event.target)) {
      this.closeAll();
    }
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAll();
    }
  }

  openItem(item) {
    const link = item.querySelector(':scope > .item-link');

    item.classList.add('open');

    if (link) {
      link.setAttribute('aria-expanded', 'true');
    }
  }

  closeItem(item) {
    const link = item.querySelector(':scope > .item-link');

    item.classList.remove('open');

    if (link) {
      link.setAttribute('aria-expanded', 'false');
    }

    item
      .querySelectorAll('.menu-item-has-children.open, .header-sub-has-children.open')
      .forEach((child) => {
        child.classList.remove('open');

        const childLink = child.querySelector(':scope > .item-link');

        if (childLink) {
          childLink.setAttribute('aria-expanded', 'false');
        }
      });
  }

  closeSiblings(item) {
    const parent = item.parentElement;

    if (!parent) return;

    parent
      .querySelectorAll(':scope > .menu-item-has-children, :scope > .header-sub-has-children')
      .forEach((sibling) => {
        if (sibling !== item) {
          this.closeItem(sibling);
        }
      });
  }

  closeAll() {
    this.nav
      .querySelectorAll('.menu-item-has-children.open, .header-sub-has-children.open')
      .forEach((item) => {
        this.closeItem(item);
      });
  }
}

function initHeaderDropdownMenu(container = document) {
  container.querySelectorAll('.box-navigation').forEach((nav) => {
    new HeaderDropdownMenu(nav);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeaderDropdownMenu();
});

document.addEventListener('shopify:section:load', (event) => {
  initHeaderDropdownMenu(event.target);
});